import { transformChannelGroupWrapper } from '~/src/dash/api/channel-group';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  EAllowStatus,
  IChannelGroup,
  IHeaderBidder,
  ISite,
  SegmentTargeting,
  transformSiteWrapper,
} from '~/src/dash/api/console-api';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import {
  ITransform,
  parseArray,
  parseDateTime,
  parseFloat,
  parseNumber,
  parseNumberAndRemoveComma,
  parseObject,
  parseString,
  transformModel,
  transformOnlyIds,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  createUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';

// ---- Configuration ----

// List of ALL the SSPs.
export const DEAL_SSP_LIST: string[] = [
  'amazonORTB',
  'appnexus',
  'beachfront',
  'cadent',
  'elementaltv',
  'indexExchange',
  'iponweb',
  'madhive',
  'magnitectv',
  'openx',
  'premion',
  'pubmatic',
  'pulsepoint',
  'rhythmone',
  'rubicon',
  'samsungAdManager',
  'samsungDSP',
  'samsungssp',
  'smaato',
  'spotx',
  'stroer',
  'sonobi',
  'sovrn',
  'telaria',
  'thetradedesk',
  'triplelift',
  'trustx',
  'vibe',
  'wurl',
  'verizon',
];

// List of all the SSPs that HAVE a third-party API.
export const DEAL_SSP_WITH_API_LIST: string[] = ['rhythmone', 'samsungssp', 'indexExchange'];

// List of all the SSPs that DOESN'T HAVE a third-party API.
export const DEAL_SSP_WITHOUT_API_LIST: string[] = [
  'amazonORTB',
  'appnexus',
  'beachfront',
  'cadent',
  'elementaltv',
  'indexExchangeNoApi',
  'iponweb',
  'madhive',
  'magnitectv',
  'openx',
  'premion',
  'pubmatic',
  'pulsepoint',
  'rhythmoneNoApi',
  'rubicon',
  'samsungAdManager',
  'samsungDSP',
  'smaato',
  'spotx',
  'stroer',
  'sonobi',
  'sovrn',
  'telaria',
  'thetradedesk',
  'triplelift',
  'trustx',
  'vibe',
  'wurl',
  'verizon',
];

// List of all SSPs that have can be used BOTH WITH AND WITHOUT a third-party API
export const DEAL_SSP_BOTH_LIST: string[] = ['rhythmone', 'indexExchange'];

// ---- Configuration end ----

// Enum for all the deal type available.
// WARNING:
//   • This enum should ALWAYS be in sync with the back-end DealType enum in src/proto/api/model.proto.
//   • This enum should ALWAYS be in sync with the DealTypeValueToDealTypeName association map object below.
export enum DealType {
  OpenAuction = 0,
  PrivateAuction = 1,
  ProgrammaticGuaranteed = 2,
  PreferredDeal = 3,
}

// Association between the DealType enum value and it's printable name.
const dealTypeValueToDealTypeName = {
  0: 'Open Auction',
  1: 'Private Auction',
  2: 'Programmatic Guaranteed',
  3: 'Preferred Deal',
};

export enum AuctionType {
  First = 'first',
  Fixed = 'fixed',
}

export enum OpenMarket {
  PrivateDeal = 0,
  OpenDeal = 1,
}

export interface IDeal {
  // Gorm fields.
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  // Object custom fields.
  Active: number;
  Name: string;
  PublisherID: number;
  BidderType: DealBidderType | ''; // SSP Type.
  DealKey: string;
  Priority: number;
  SalespersonID: number;
  SeatIDs: string[] | null;

  // Targeting.
  AudienceTargeting: string; // comma-delimited liveramp audienceIds
  AudienceTargetingRelationshipType: EAllowStatus; // Inclusion or Exclusion
  AudienceTargetingOperationType: SegmentTargeting; // Union or Intersection
  SspParams: IUnrulyDealParams | ISamsungDealParams | IIndexExchangeToPublicaApiDeal;
  DealType: DealType;
  StartDate: string;
  EndDate: string;
  ImpressionGoal: string;
  BidFloor: string;
  CurrencyDisplayed?: ECurrencyType;

  // Nested objects.
  HeaderBidders: IHeaderBidder[];
  Channels: ISite[] | ISmallSite[];
  ChannelGroups: IChannelGroup[];
  ContextualSegmentIDS: number[];
}

interface ISspParams {
  Description: string;
  DealId: number;
}

export interface IIXSite {
  name: string;
  id: number;
}

export interface IIXInventoryGroup {
  name: string;
  id: number;
}

export interface ISiteAndInventoryGroup {
  sites: IIXSite[];
  inventory_groups: IIXInventoryGroup[];
}

export interface ISeatGroup {
  seats: IIXSeat[];
}

export interface IIXSeat {
  buyerID: number;
  name: string;
  seatID: number;
  status: string;
}

export interface IUnrulyDealParams extends ISspParams {
  // Deal Details
  Buyers: IUnrulyDealBuyer[];
  RhythmoneDealID: number;
  PackageID: number;
  StartDate: string;
  EndDate: string;
  PlatformFeeType: number;
  OverallImpressionLimit: number;
  DailyImpressionLimit: number;
  UseSupplyFloor: boolean;
  DailyRequestLimit: number;
  EnableDealPacing: boolean;
  Dayparting: string;
}

export type SamsungDealStatus = 'PAUSED' | 'STOPPED' | 'RUNNING' | 'PENDING' | 'DELETED';

export type IndexExchangeDealStatus = 'ACTIVE' | 'PAUSED';

export interface ISamsungDealParams extends ISspParams {
  ecpm: number | string;
  partnerName: string;
  status: SamsungDealStatus;
  totalAdvBudget: number | string;
  isDealStopped: boolean;
}

export interface IUnrulyDealBuyer {
  DspID: number;
  SeatID: string;
  Adomain: string;
  BidFloor: number;
  Active: boolean;
}

export interface IIndexExchangeToPublicaApiDeal {
  // Required
  dealID: string;
  dspID: number;
  name: string;
  startDate: string;
  endDate: string;
  auctionType: string;
  rate: number;

  // Optional
  dealType: string;
  priority: number;
  openMarket: number;
  payout: number;
  humanVerification: number;
  // placementID: number; TODO
  buyerID: number;
  seatIDs: number[];

  // Only one of these fields is required, cannot have both
  targetingSiteIDs: number[];
  targetingInventoryGroupIDs: number[];
}

export interface IDealTPAccount {
  ID: number;
  ThirdPartyUserID: string;
  APIName: string;
  Username: string;
  Password: string;
  Error: string; // This isn't really a part of the object, but it can be added by the backend to give error info.
}

export const dealBidderTypeRenamingMap = {
  rhythmoneNoApi: 'rhythmone',
  indexExchangeNoApi: 'indexExchange',
} as Record<DealBidderType, string>;

export type DealBidderType =
  | 'indexExchange'
  | 'rhythmone'
  | 'pubmatic'
  | 'smaato'
  | 'spotx'
  | 'beachfront'
  | 'appnexus'
  | 'triplelift'
  | 'telaria'
  | 'rubicon'
  | 'verizon'
  | 'iponweb'
  | 'samsungssp'
  | 'rhythmoneNoApi'
  | 'indexExchangeNoApi'
  | 'thetradedesk';

export const getDealBidderType = (bidderType: DealBidderType): string => {
  return bidderType in dealBidderTypeRenamingMap
    ? dealBidderTypeRenamingMap[bidderType]
    : bidderType;
};

// API response format when querying the deal's bid request flag
export interface IPublicaDealHasMadeBidRequest {
  DealHasMadeBidRequest: boolean;
}

const transformDealTPAccountFromApiConfig: ITransform = {
  ID: parseNumber(['ID']),
  ThirdPartyUserID: parseString(['ThirdPartyUserID']),
  APIName: parseString(['APIName']),
  Username: parseString(['Username']),
  Password: parseString(['Password']),
  Error: parseString(['Error']),
};

const transformDealTPAccountToApiConfig: ITransform = {
  ID: parseNumber(['ID']),
  ThirdPartyUserID: parseString(['ThirdPartyUserID']),
  APIName: parseString(['APIName']),
  Username: parseString(['Username']),
  Password: parseString(['Password']),
  Error: parseString(['Error']),
};

// |------------------ DEAL Transformations ------------------|
const transformDealToApiConfig: ITransform = {
  // Gorm fields.
  ID: parseNumber(['ID']),

  // Object custom fields.
  Active: parseNumber(['Active']),
  Name: parseString(['Name']),
  PublisherID: parseNumber(['PublisherID']),
  BidderType: parseString(['BidderType']),
  DealKey: parseString(['DealKey']),
  Priority: parseNumber(['Priority']),
  SalespersonID: parseNumber(['SalespersonID']),
  SeatIDs: parseArray(['SeatIDs'], (a: string) => a),

  // Targeting.
  AudienceTargeting: parseString(['AudienceTargeting']),
  AudienceTargetingRelationshipType: parseNumber(['AudienceTargetingRelationshipType']),
  AudienceTargetingOperationType: parseNumber(['AudienceTargetingOperationType']),
  SspParams: parseObject(['SspParams'], JSON.stringify),
  DealType: parseNumber(['DealType']),
  StartDate: parseDateTime(['StartDate']),
  EndDate: parseDateTime(['EndDate']),
  ImpressionGoal: parseNumberAndRemoveComma(['ImpressionGoal']),
  BidFloor: parseFloat(['BidFloor'], 0),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency_displayed']),

  // Nested objects.
  Channels: parseArray(['Channels'], transformOnlyIds),
  ChannelGroups: parseArray(['ChannelGroups', 'channelgroups'], transformOnlyIds),
  ContextualSegmentIDS: parseArray(['ContextualSegmentIDS'], (obj: any) => {
    return obj;
  }),
};

const transformDealFromApiConfig: ITransform = Object.assign({}, transformDealToApiConfig, {
  Channels: parseArray(['Channels'], transformSiteWrapper),
  HeaderBidders: parseArray(['HeaderBidders'], (a: any) => a),
  ChannelGroups: parseArray(['ChannelGroups', 'channelgroups'], transformChannelGroupWrapper),
  SspParams: parseString(['SspParams'], JSON.parse),
  ContextualSegmentIDS: parseArray(['ContextualSegments'], (obj: any) => {
    return obj.ID;
  }),
});

// |------------------ Deal API CALLS ------------------|
export const transformDealToApi = transformModel<IDeal>(transformDealToApiConfig);
export const transformDealFromApi = transformModel<IDeal>(transformDealFromApiConfig);

// GET all deals for this publisher
// /publishers/{publisherID}/deals
export async function getNonThirdPartyDeals(publisherID: string): Promise<IDeal[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deals`);
  return await restGet<IDeal[]>(reqUrl, {}).then((val: IHttpResponse<IDeal[]>) => {
    return val.parsedBody ? (val.parsedBody as IDeal[]).map(transformDealFromApi) : [];
  });
}

// GET unruly deals for this publisher
// /publishers/{publisherID}/unruly_deals
export async function getUnrulyDeals(publisherID: string): Promise<IDeal[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/unruly_deals`);
  return await restGet<IDeal[]>(reqUrl, {}).then((val: IHttpResponse<IDeal[]>) => {
    return val.parsedBody ? (val.parsedBody as IDeal[]).map(transformDealFromApi) : [];
  });
}

// GET samsung deals for this publisher
// /publishers/{publisherID}/samsung_deals
export async function getSamsungDeals(publisherID: string): Promise<IDeal[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/samsung_deals`);
  return await restGet<IDeal[]>(reqUrl, {}).then((val: IHttpResponse<IDeal[]>) => {
    return val.parsedBody ? (val.parsedBody as IDeal[]).map(transformDealFromApi) : [];
  });
}

// get all deals
export const getAllDeals = (publisherID: string) =>
  // @ts-ignore
  Promise.allSettled([
    getNonThirdPartyDeals(publisherID),
    getUnrulyDeals(publisherID),
    getSamsungDeals(publisherID),
  ]);

// GET, PATCH, DELETE a single deal
// /publishers/{publisherID}/deal/{dealID}
export async function getDeal(dealID: string, publisherID: string): Promise<IDeal> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deal/${dealID}`);
  return await restGet<IDeal>(reqUrl, {}).then((val: IHttpResponse<IDeal>) => {
    return transformDealFromApi(val.parsedBody);
  });
}

// NOTE: Because a full object is required, make sure that all fields in the object are defined in the interface/transform
export async function updateDeal(
  deal: IDeal,
  publisherID: string,
  fullUpdate: boolean,
): Promise<IDeal> {
  const partialUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deal/${deal.ID}`);
  const reqUrl = fullUpdate ? partialUrl + '&full_update=true' : partialUrl;
  const parsedDeal = transformDealToApi(deal);
  return await restPatch<IDeal>(reqUrl, fullUpdate ? parsedDeal : deal).then(
    (val: IHttpResponse<IDeal>) => {
      return transformDealFromApi(val.parsedBody);
    },
  );
}

export async function deleteDeal(
  dealID: number,
  publisherID: string,
): Promise<IHttpResponse<{ status: boolean }>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deal/${dealID}`);
  return await restDelete<{ status: boolean }>(reqUrl, undefined);
}

// POST a single deal
// /publishers/{publisherID}/deal
export async function createDeal(deal: IDeal, publisherID: string): Promise<IDeal> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deal`);
  const parsedDeal = transformDealToApi(deal);
  return await restPost<IDeal>(reqUrl, parsedDeal).then((val: IHttpResponse<IDeal>) => {
    return transformDealFromApi(val.parsedBody);
  });
}

// Retrieves from the API if the given deal has made at least 1 bid request.
// If the deal has made at least 1 bid request, the flag is true, else the flag is false.
export async function GetPublicaDealBidRequestFlag(
  publisherID: string,
  dealID: number,
): Promise<IPublicaDealHasMadeBidRequest> {
  // prepare the URL
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/deal/${dealID}/bid_request_flag`,
  );
  // do the request
  return await restGet<IPublicaDealHasMadeBidRequest>(reqUrl, {}).then(
    (val: IHttpResponse<IPublicaDealHasMadeBidRequest>) => {
      return val.parsedBody
        ? (val.parsedBody as IPublicaDealHasMadeBidRequest)
        : { DealHasMadeBidRequest: false };
    },
  );
}

// |------------------ Deal third-party accounts API CALLS ------------------|
export const transformDealTPAccountFromApi = transformModel<IDealTPAccount>(
  transformDealTPAccountFromApiConfig,
);
export const transformDealTPAccountToApi = transformModel<IDealTPAccount>(
  transformDealTPAccountToApiConfig,
);

export async function getDealTPAccounts(publisherID: string): Promise<IDealTPAccount[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deal_tp_accounts`);
  return await restGet<IDealTPAccount[]>(reqUrl, {}).then(
    (val: IHttpResponse<IDealTPAccount[]>) => {
      return val.parsedBody
        ? (val.parsedBody as IDealTPAccount[]).map(transformDealTPAccountFromApi)
        : [];
    },
  );
}

export async function updateDealTPAccounts(
  dealTPAccounts: IDealTPAccount[],
  publisherID: string,
): Promise<IDealTPAccount[]> {
  const partialUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/deal_tp_accounts`);
  const parsedDealTPAccounts = dealTPAccounts.map(transformDealTPAccountToApi);
  return await restPatch<IDealTPAccount[]>(partialUrl, parsedDealTPAccounts).then(
    (val: IHttpResponse<IDealTPAccount[]>) => {
      return val.parsedBody
        ? (val.parsedBody as IDealTPAccount[]).map(transformDealTPAccountFromApi)
        : [];
    },
  );
}

// |------------------ Index Exchange Site and Inventory Group API CALLS ------------------|
// GET index exchange site and inventory group ids
// /publishers/{publisherID}/index_exchange/get_site_ids
export async function getIndexExchangeSiteIDs(
  publisherID: string,
): Promise<[ISiteAndInventoryGroup | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherID + '/index_exchange/get_site_ids',
    publisherID,
  );

  try {
    return [
      (await restGet<ISiteAndInventoryGroup>(reqUrl)).parsedBody as ISiteAndInventoryGroup,
      undefined,
    ];
  } catch (e) {
    return [undefined, e];
  }
}

// |------------------ Index Exchange Seat Group API CALL ------------------|
// GET index exchange seat group ids
// /publishers/{publisherID}/index_exchange/get_seat_ids/{dspID}
export async function getIndexExchangeSeatIDs(
  publisherID: string,
  dspID: number,
): Promise<[ISeatGroup | undefined, string | undefined]> {
  const reqUrl = createUrl(
    '/publishers/' + publisherID + '/index_exchange/get_seat_ids/' + dspID,
    publisherID,
  );

  try {
    return [(await restGet<ISeatGroup>(reqUrl)).parsedBody as ISeatGroup, undefined];
  } catch (e) {
    return [undefined, e];
  }
}

// |------------------ Deal helper functions ------------------|

// Transforms a DealType enum value to a printable name.
export function getDealTypeNameFromDealTypeValue(dealTypeValue: DealType): string {
  const name = dealTypeValueToDealTypeName[dealTypeValue];
  if (!name) {
    return '';
  }
  return name;
}
